/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'react',
	'prop-types',
	'mobx-react',
	'@ba/ba-ui-carbon-toolkit',
	'mobx'
], function (React, PropTypes, MobxReact, Toolkit, Mobx) {

	const { observer } = MobxReact;
	const { Component, Fragment } = React;
	const { Breadcrumb } = Toolkit;

	const STYLE_DISPLAY_NONE = 'none';
	const STYLE_DISPLAY = '';
	const BREADCRUMB_FONT_SIZE = 'large';

	class PropertiesView extends Component {

		constructor(props){
			super(props);
			this.containers = [];
			this.state = {
				updateToken : false
			};
		}

		render() {
			const activeContainerId = this._getActiveContainerId();
			//Cache parental container for navigating back
			if (activeContainerId && !this.containers.find(container => container.id === activeContainerId)) {
				this.containers.push({
					id: activeContainerId,
					display: this._buildContainerDisplay()
				});
			}
			//Do not display inactive container
			const containersDisplay = this.containers.map((entry, i) => {
				const styles = {
					display: entry.id === activeContainerId ? STYLE_DISPLAY : STYLE_DISPLAY_NONE
				};
				return (
					<div key={i} style={styles} >
						{ entry.display }
					</div>
				);
			});
			return (
				<Fragment>
					{this.buildNavDisplay()}
					{containersDisplay}
				</Fragment>);
		}

		componentDidMount() {
			this._reactionDispose = Mobx.reaction(
				/*When selected context changes, the view should clear its cache*/
				() => this.props.selectedContentIds.join(),
				() => {
					this.containers = [];
				});
		}


		componentWillUnmount(){
			if(this._reactionDispose){
				this._reactionDispose();
			}
		}

		_buildContainerDisplay(){
			const propertyControls = this.props.propertyControlsFactory.makeControls(this.props.propertyList, this.props.propertyLayoutList);
			return (
				<div className="v2-properties-control-view">
					{propertyControls}
				</div>
			);
		}

		buildNavDisplay() {
			const container = this._getContextContainer(this._getActiveContainerId());
			if (container && container.ancestors && container.ancestors.length) {
				const ancestors = [];
				container.ancestors.forEach((containerId)=>{
					const curContainer = this._getContextContainer(containerId);
					if(curContainer){
						ancestors.push({
							id: curContainer.id,
							defaultName: curContainer.label
						});
					}
				});
				ancestors.push({
					id: container.id,
					defaultName: container.label
				});
				const props = {
					ancestors: ancestors,
					navCallback: index => this.navigate(ancestors[index].id),
					historyCallback: () => {},
					size: BREADCRUMB_FONT_SIZE,
					hideBackButton: true,
					style: {}
				};
				return (<Breadcrumb { ...props } />);
			}
			return null;
		}

		navigate(targetId) {
			const contextContainers = this.props.context.containers;
			let index = contextContainers.length;
			const removedContainers = [];
			while(index--){
				if(contextContainers[index].ancestors.indexOf(targetId) >= 0){
					//Remove all offspring containers of the target container
					const removedContainer = contextContainers.splice(index, 1);
					removedContainers.push(removedContainer[0].id);
				}
			}
			this.containers = this.containers.filter(container => (removedContainers.indexOf(container.id) === -1));
			this.props.context.activeContainer = targetId;
			this._updateRenderToken();
		}

		_updateRenderToken(){
			this.setState({ updateToken: !this.state.updateToken });
		}

		_getActiveContainerId(){
			return this.props.context && this.props.context.activeContainer;
		}

		_getContextContainer(containerId){
			if(this.props.context && this.props.context.containers){
				return this.props.context.containers.find(container => container.id === containerId);
			}
			return null;
		}
	}

	PropertiesView.propTypes = {
		selectedContentIds: PropTypes.array.isRequired,
		context: PropTypes.object.isRequired,
		propertyList: PropTypes.array.isRequired,
		propertyLayoutList: PropTypes.array.isRequired,
		propertyControlsFactory: PropTypes.object.isRequired
	};

	PropertiesView.defaultProps = {};

	return observer(PropertiesView);
});